'use client';
import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';
// Product
import { Breadcrumbs } from 'components/global/breadcrumbs';
import { ProductBanner } from '../components/product/banner';
import { BuyBox } from '../components/product/buy-box';
import { ProductDescription } from '../components/product/description';
import { ProductInventory } from '../components/product/inventory';
import { MediaCarousel } from '../components/product/media-carousel';
import { OptionSelector } from '../components/product/option-selector';
import { ProductPrice } from '../components/product/price';
import { ShippingDelivery } from '../components/product/shipping-delivery';
import { ShippingRestriction } from '../components/product/shipping-restriction';
import { ProductSKU } from '../components/product/sku';
import { ProductSpecs } from '../components/product/specs';
import { ProductTitle } from '../components/product/title';
// Global Product Card
import { AddWishlist } from 'components/login/add-to-wishlist';
import { Mulberry } from 'components/product/mulberry';

// ------------------
// Product Components
// ------------------
Builder.register('insertMenu', {
  name: 'PDP Components',
  items: [
    { name: 'PDP Add to Cart' },
    { name: 'Banner' },
    { name: 'PDP Breadcrumbs'},
    { name: 'Description' },
    { name: 'Inventory' },
    { name: 'Media Carousel' },
    { name: 'Options' },
    { name: 'Price' },
    { name: 'Ratings' },
    { name: 'Reviews' },
    { name: 'Shipping Delivery' },
    { name: 'Shipping Restriction' },
    { name: 'SKU' },
    { name: 'Specs' },
    { name: 'Title' },
    { name: 'Wishlist' },
    { name: 'Mulberry' },
  ],
  priority: 2,
});

Builder.registerComponent(Mulberry,
  {
    models: ['product-detail-page'],
    name: 'Mulberry',
    noWrap: true,
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/awards.svg',
    description:
      'Mulberry for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(AddWishlist, {
  models: ['product-detail-page'],
  name: 'Wishlist',
  image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/heart.svg',
  description: 'Add to Wishlist for Product Detail Page',
  defaults: {
    bindings: {
      'component.options.product': 'state.product'
    }
  }
});

Builder.registerComponent(ShippingRestriction,
  {
    models: ['product-detail-page'],
    name: 'Shipping Restriction',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/format-text.svg',
    description: 'Product shipping restriction for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(ShippingDelivery,
  {
    models: ['product-detail-page'],
    name: 'Shipping Delivery',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/format-text.svg',
    description: 'Product shipping delivery for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(ProductSKU,
  {
    models: ['product-detail-page'],
    name: 'SKU',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/format-text.svg',
    description: 'Product SKU for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(ProductSpecs,
  {
    models: ['product-detail-page'],
    name: 'Specs',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/format-text.svg',
    description: 'Product specs for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(ProductTitle,
  {
    models: ['product-detail-page'],
    name: 'Title',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/format-text.svg',
    description: 'Product title for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(dynamic(() => import('../components/product/reviews').then((m) => m.ProductReviews), {
  ssr: false
}),
  {
    models: ['product-detail-page'],
    name: 'Reviews',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/comment.svg',
    description: 'Reviews for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(dynamic(() => import('../components/product/ratings').then((m) => m.ProductRatings), {
  ssr: false
}),
  {
    models: ['product-detail-page'],
    name: 'Ratings',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/smile.svg',
    description: 'Ratings for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(ProductPrice,
  {
    models: ['product-detail-page'],
    name: 'Price',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/dollar.svg',
    description: 'Price for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(OptionSelector,
  {
    models: ['product-detail-page'],
    name: 'Options',
    noWrap: true,
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/options.svg',
    description: 'Option selector for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(MediaCarousel,
  {
    models: ['product-detail-page'],
    name: 'Media Carousel',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/image.svg',
    description: 'Product media carousel for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(BuyBox,
  {
    models: ['product-detail-page'],
    name: 'PDP Add to Cart',
    image: 'https://cdn.builder.io/api/v1/image/assets/IsxPKMo2gPRRKeakUztj1D6uqed2/81a15681c3e74df09677dfc57a615b13',
    description: 'Component with CTA & quantity selector for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    },
    inputs: [
      {
        name: 'showQuantitySelector',
        friendlyName: 'Show Quantity Selector',
        type: 'boolean',
        defaultValue: true
      },
      {
        name: 'showStickyBar',
        friendlyName: 'Show Sticky Bar',
        type: 'boolean',
        defaultValue: true
      }
    ]
  }
);

Builder.registerComponent(ProductBanner,
  {
    models: ['product-detail-page'],
    name: 'Banner',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/awards.svg',
    description:
      'Product banner for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(Breadcrumbs, {
  models: ['product-detail-page'],
  name: 'PDP Breadcrumbs',
  defaults: {
    bindings: {
      'component.options.foundCat': 'state.foundCat',
      'component.options.notFoundCat': 'state.notFoundCat'
    }
  }
});

Builder.registerComponent(ProductDescription,
  {
    models: ['product-detail-page'],
    name: 'Description',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/format-text.svg',
    description: 'Product description for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);

Builder.registerComponent(ProductInventory,
  {
    models: ['product-detail-page'],
    name: 'Inventory',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/format-text.svg',
    description:
      'Product inventory for Product Detail Page',
    defaults: {
      bindings: {
        'component.options.product': 'state.product'
      }
    }
  }
);